import {
  DiscoverySupplierListResponseType,
  DiscoveryV2ListResponseType,
  BuyerProductGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  findLatestPreOrderAvailableFromDate,
  formatCurrency,
} from "@sellar-io/common";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "use-debounce";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import {
  Icon,
  ProfilePicture,
  SpinnerLegacy,
  StockIndicator,
} from "../../../components";
import { Badge, BadgePurple } from "../../../components/badge/Badge";
import {
  BestPricePromise,
  BestPricePromiseFullSize,
  BestPricePromiseCompact,
} from "../../../components/bestPricePromise/BestPricePromise";
import { BestPricePromiseDialog } from "../../../components/bestPricePromise/BestPricePromiseDialog";
import { InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { IncompleteListUi } from "../../../components/incompleteListUi/IncompleteListUi";
import { IntersectingInfiniteLoadingButton } from "../../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { RouteScrollArea } from "../../../components/scrollRestoration/RouteScrollArea";
import { QuantityIndicator } from "../../../components/quantityIndicator/QuantityIndicator";
import { Tooltip } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { SlideTransition } from "../../../components/transition/SlideTransition";
import { DISCOVER, ORDER_BUYER } from "../../../constants/routes";
import { useEvents } from "../../../hooks";
import { useDiscoverySupplierList } from "../../../hooks/api/v2/discovery/useDiscoverySupplierList";
import { useDiscoveryV2ProductList } from "../../../hooks/api/v2/discovery/useDiscoveryV2ProductList";
import { useDisplaySizeVariant } from "../../../hooks/useDisplaySizeVariant";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { getSellarWebsiteUtms } from "../../../utils/utmHelper";
import { useConsolidatedOrderingSearchIntercom } from "./distribution/useConsolidatedOrderingSearchIntercom";
import { DiscoveryMobileFilterDrawer } from "./components/DiscoveryMobileFilterDrawer";
import { DiscoverySuggestionTag } from "./components/DiscoverySuggestionTag";
import { DiscoveryV2ProductDialog } from "./components/DiscoveryV2ProductDialog";
import { randomizeDiscoverySuggestionTags } from "./discoverySuggestionTags";
import { DiscoverySuggestionTagContainer } from "./DiscoveryV2";
import { DiscoveryMarketSearch } from "../components/DiscoveryMarketSearch";
import {
  setRecentlyViewedExpiry,
  useDiscoveryV2RecentlyViewedStore,
} from "./DiscoveryV2RecentlyViewedStore";
import {
  orderByTypes,
  useDiscoveryV2SearchStore,
} from "./DiscoveryV2SearchStore";
import { OrderBySelector } from "./lib/OrderBySelector";
import {
  DiscoverySortComparison,
  DiscoverySortComparisonEntry,
} from "./lib/DiscoverySortComparison";
import { useDiscoveryV2SearchProductListParams } from "./lib/useDiscoveryV2SearchProductListParams";
import { FilterAbvCard } from "./filterCards/FilterAbvCard";
import { FilterCard, FilterSwitch } from "./filterCards/FilterCard";
import { FilterContainerCard } from "./filterCards/FilterContainerCard";
import { FilterDietaryCard } from "./filterCards/FilterDietaryCard";
import { FilterPriceCard } from "./filterCards/FilterPriceCard";
import { PriceTypeCategory } from "./filterCards/FilterPriceCard.shared";
import { FilterStyleCard } from "./filterCards/FilterStyleCard";
import { FilterSupplierCard } from "./filterCards/FilterSupplierCard";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardImage,
  H4,
  H5,
  H6,
  SellarIconGradient,
  InputIcon,
} from "@sellar-io/components";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import dayjs from "dayjs";
import { DietaryDisplayList } from "../../../components/dietaryDisplayList/DietaryDisplayList";
import { DiscoveryV2AddToBasketCta } from "./distribution/DiscoveryV2AddToBasketCta";
import { DraftBasketDrawer } from "./distribution/DraftBasketDrawer";
import { MarketBasketFeedbackProvider } from "./distribution/MarketBasketFeedbackContext";
import { MarketBasketFlyover } from "./distribution/MarketBasketFlyover";
import { useDraftDbsBaskets } from "./distribution/useDraftDbsBaskets";
import {
  ConsolidatedBasketOpenSource,
  useConsolidatedBasketTracking,
} from "./distribution/consolidatedOrderingTracking";
import { useCanShowDeliveredBySellarOptionV2 } from "./lib/useCanShowDeliveredBySellarOptionV2";
import calendar from "dayjs/plugin/calendar";
import { DeliveredBySellarOptionV2 } from "./lib/DeliveredBySellarOptionV2";
import {
  DeliveredBySellarHeaderWithCutOffToggle,
  DeliveredBySellarMarketHeader,
  SingleSupplierSearchDialogContent,
  SingleSupplierSearchDialogProvider,
} from "./lib/DeliveredBySellarMarketHeader";
import { zLayer, FloatingLayerProvider } from "@sellar-io/components";

dayjs.extend(calendar);

const renderFilterIcon = ({
  menuActive,
  filterCount,
}: {
  menuActive: boolean;
  filterCount: number;
}) => {
  if (menuActive) return <Icon icon={"CLOSE"} />;
  if (filterCount) return <QuantityIndicator quantity={filterCount} />;
  return <Icon icon={"FILTER"} />;
};

export const DiscoveryV2Search = () => {
  const canShowDeliveredBySellarOptionV2 =
    useCanShowDeliveredBySellarOptionV2();

  const {
    menuActive,
    searchValue,
    selectedSuppliers,
    selectedContainerTypes,
    abvRange,
    isSellarCreditEligibleOnly,
    isDirectDelivery,
    orderBy,
    isDeliveredBySellar,
    deliveredBySellarOption,
    orderCutOffAt,
    setMenuActive,
    setSearchValue,
    reset,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      menuActive: state.menuActive,
      setMenuActive: state.setMenuActive,
      setSearchValue: state.setSearchValue,
      searchValue: state.searchValue,
      selectedSuppliers: state.selectedSuppliers,
      selectedContainerTypes: state.selectedContainerTypes,
      abvRange: state.abvRange,
      isSellarCreditEligibleOnly: state.isSellarCreditEligibleOnly,
      isDirectDelivery: state.isDirectDelivery,
      orderBy: state.orderBy,
      isDeliveredBySellar: state.isDeliveredBySellar,
      deliveredBySellarOption: state.deliveredBySellarOption,
      orderCutOffAt: state.orderCutOffAt,
      reset: state.reset,
    })),
  );

  const [drawerActive, setDrawerActive] = useState<boolean>(false);
  const [isComparingSort, setIsComparingSort] = useState(false);
  const compareSortEnabled = useFeatureFlagStore().isActive(
    "DISCOVERY_SORT_COMPARISON",
  );
  const showSortComparison = compareSortEnabled && isComparingSort;
  const [draftBasketDrawerActive, setDraftBasketDrawerActive] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const isMobile = useMediaQuery("(max-width: 700px)");
  const isDesktop = useMediaQuery("(min-width: 1084px)");
  const autocompleteEnabled = useRemoteFeatureFlags().isActive(
    "marketSearchAutocomplete",
  );
  const sdConsolidatedCheckoutEnabled = useRemoteFeatureFlags().isActive(
    "sdConsolidatedCheckout",
  );
  const canShowDeliveredBySellar = useCanShowDeliveredBySellarOptionV2();
  const { totalItemCount, baskets } = useDraftDbsBaskets();
  const { trackBasketOpened } = useConsolidatedBasketTracking();
  const openBasket = (source: ConsolidatedBasketOpenSource) => {
    trackBasketOpened(baskets, source);
    setDraftBasketDrawerActive(true);
  };
  const filterCountArray: number[] = [
    searchValue ? 1 : 0,
    selectedSuppliers.length,
    selectedContainerTypes.length,
    abvRange ? 1 : 0,
  ];

  const filterCount = filterCountArray.reduce((curr, acc) => curr + acc, 0);

  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );

  const buyerPostcode =
    business?.type === "retailer" ? business.DeliveryAddress.postalCode : "";

  const supplierSearchEnabled =
    !!searchValue.trim() && !selectedContainerTypes.length;

  const discoverySupplierListQuery = useDiscoverySupplierList(
    {
      limit: 10,
      search: searchValue,
      query: [
        ...(isDirectDelivery
          ? ([
              {
                type: "directDelivery",
                modifier: "EQUALS",
                value: isDirectDelivery,
              },
            ] as const)
          : []),
        ...(isDeliveredBySellar
          ? ([
              {
                type: "deliveredBySellar",
                modifier: "EQUALS",
                value: isDeliveredBySellar,
              },
            ] as const)
          : []),
        ...(isSellarCreditEligibleOnly
          ? ([
              {
                type: "sellarCreditEligibleOnly",
                modifier: "EQUALS",
                value: isSellarCreditEligibleOnly,
              },
            ] as const)
          : []),
      ],
      unAuthorisedUserPostcode,
      retailerPostcode: buyerPostcode,
    },
    {
      enabled: supplierSearchEnabled,
    },
  );

  const productListParams = useDiscoveryV2SearchProductListParams();
  const recommendedSortEnabled = useRemoteFeatureFlags().isActive(
    "recommendedDiscoverySearch",
  );

  const discoveryListQuery = useDiscoveryV2ProductList(
    {
      ...productListParams,
      sortBy:
        recommendedSortEnabled && orderBy === "popular"
          ? "recommended"
          : orderBy,
    },
    { enabled: !showSortComparison },
  );

  const { track } = useEvents();

  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);
  const [bestPricePromiseProduct, setBestPricePromiseProduct] =
    useState<BuyerProductGetResponseType>();
  const { sessionId, journeySource } = useMarketStorefrontSessionId();
  const resultCount = discoveryListQuery.data?.pages.flat().length ?? 0;
  const {
    onSearchChanged: trackSearchChanged,
    onSearchSubmitted: trackSearchSubmitted,
    onFilterChanged: trackFilterChanged,
  } = useConsolidatedOrderingSearchIntercom({
    sessionId,
    journeySource,
    isDeliveredBySellar,
    isLoading: discoveryListQuery.isFetching,
    resultCount,
    selectedSupplierCount: selectedSuppliers.length,
    deliveryFilter: deliveredBySellarOption === "weekend" ? "weekend" : "all",
    orderCutoffAt: orderCutOffAt || undefined,
  });
  useEffect(() => {
    if (debouncedSearchValue.length) {
      track("Discover term searched", {
        text: debouncedSearchValue,
        ...(sessionId ? { sessionId } : {}),
        isDeliveredBySellar,
        resultCount,
        ...getSellarWebsiteUtms(),
      });
    }
  }, [
    debouncedSearchValue,
    isDeliveredBySellar,
    resultCount,
    sessionId,
    track,
  ]);

  useEffect(() => {
    if (!isDeliveredBySellar || discoveryListQuery.isFetching) return;

    track("Delivered By Sellar - Search Viewed", {
      ...(sessionId ? { sessionId } : {}),
      journeySource,
      deliveryFilter: deliveredBySellarOption,
      ...(orderCutOffAt ? { orderCutoffAt: orderCutOffAt } : {}),
      selectedSupplierCount: selectedSuppliers.length,
      resultCount,
    });
  }, [
    deliveredBySellarOption,
    discoveryListQuery.isFetching,
    isDeliveredBySellar,
    journeySource,
    orderCutOffAt,
    resultCount,
    selectedSuppliers.length,
    sessionId,
    track,
  ]);

  const { isDisplaySizeSatisfied, target } = useDisplaySizeVariant();
  const is5xl = isDisplaySizeSatisfied("5xl");

  const { selectedProductId, selectedProductParentId, setSelectedProductIds } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        selectedProductId: state.selectedProductId,
        selectedProductParentId: state.selectedProductParentId,
        setSelectedProductIds: state.setSelectedProductIds,
      })),
    );
  const isDialogActive = !!selectedProductId;

  useEffect(
    () => () => {
      setSelectedProductIds(undefined);
    },
    [setSelectedProductIds],
  );

  const canShowSingleSupplierHeader =
    selectedSuppliers.length === 1 && isDeliveredBySellar;
  return (
    <MarketBasketFeedbackProvider onViewBasket={openBasket}>
      <div className="@container flex h-full min-h-0 flex-col" ref={target}>
        <div className="shrink-0">
          <NavigationBar
            leftIcon={"CHEVRON_LEFT"}
            onClickLeftIcon={() => {
              if (menuActive) return setMenuActive(false);
              navigate(DISCOVER);
              reset();
            }}
            title={"Search"}
            rightComponent={
              business?.type === "retailer" ? (
                <MarketBasketFlyover onViewBasket={openBasket}>
                  <div
                    className="flex cursor-pointer items-center gap-1"
                    onClick={() => openBasket("navbar")}
                  >
                    <Icon icon="CART_OUTLINE" size={1} />
                    {canShowDeliveredBySellar && totalItemCount > 0 && (
                      <QuantityIndicator
                        quantity={totalItemCount}
                        className="bg-[#A34FD2]"
                      />
                    )}
                  </div>
                </MarketBasketFlyover>
              ) : (
                <BestPricePromise
                  onClick={() => setBestPricePromiseDialogActive(true)}
                >
                  {isMobile ? (
                    <BestPricePromiseCompact />
                  ) : (
                    <BestPricePromiseFullSize />
                  )}
                </BestPricePromise>
              )
            }
          />
        </div>
        <RouteScrollArea
          restorationEnabled={
            !discoveryListQuery.isLoading &&
            !discoverySupplierListQuery.isLoading
          }
          className="[container-type:size] flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
        >
          <DeliveredBySellarMarketHeader />
          {!isDesktop ? (
            <>
              <DiscoveryMobileFilterDrawer
                drawerActive={drawerActive}
                setDrawerActive={setDrawerActive}
                onFilterChanged={trackFilterChanged}
              />
              <div className="z-50 flex w-full shrink-0 flex-col gap-2 bg-neutral-100 px-4 py-4">
                <div className="flex w-full flex-row gap-2">
                  {autocompleteEnabled ? (
                    <DiscoveryMarketSearch
                      fullWidth
                      initialValue={searchValue}
                      onSubmit={setSearchValue}
                      onCommit={(selection) =>
                        trackSearchSubmitted(selection.label)
                      }
                    />
                  ) : (
                    <InputContainer className={"w-full"}>
                      <InputText
                        required={false}
                        placeholder={`Search...`}
                        value={searchValue}
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          trackSearchChanged(e.target.value);
                        }}
                      />
                      <InputIcon icon={"MAGNIFY"} />
                    </InputContainer>
                  )}
                  <Button
                    color={"secondary"}
                    onClick={() => setDrawerActive(true)}
                  >
                    <Icon icon={"FILTER_VARIANT"} />
                  </Button>
                </div>
                {canShowDeliveredBySellarOptionV2 &&
                  !canShowSingleSupplierHeader && (
                    <DeliveredBySellarHeaderWithCutOffToggle isMobile={true} />
                  )}
              </div>
            </>
          ) : null}
          <div
            className={twMerge(
              "mx-auto grid w-full max-w-[1200px] flex-1 grid-cols-1 items-start gap-2 px-4 @5xl:grid-cols-[350px_minmax(0,1fr)]",
              !is5xl && menuActive && "overflow-hidden",
            )}
          >
            {isDesktop ? (
              <FloatingLayerProvider host="pinned">
                <div
                  className={`sticky top-0 ${zLayer.pinned} flex h-[100cqh] min-h-0 flex-col gap-2 self-start overflow-hidden py-4 @5xl:py-2`}
                >
                  <FilterCard
                    className={"@5xl:hidden"}
                    onClick={() => setMenuActive(!menuActive)}
                  >
                    <H5 className={"uppercase"}>Filter</H5>{" "}
                    {renderFilterIcon({ menuActive, filterCount })}
                  </FilterCard>
                  <SlideTransition
                    show={is5xl ? true : menuActive}
                    animate={!is5xl}
                    parentClass="min-h-0 flex-1"
                    className="flex h-full min-h-0 flex-col @5xl:gap-2"
                  >
                    <FilterMenu
                      searchValue={searchValue}
                      setSearchValue={setSearchValue}
                      onSearchSubmitted={trackSearchSubmitted}
                      onFilterChanged={trackFilterChanged}
                    />
                  </SlideTransition>
                </div>
              </FloatingLayerProvider>
            ) : null}

            <div
              className={twMerge(
                "flex min-h-0 flex-col gap-2 pt-4 transition-opacity @5xl:pt-2",
                menuActive ? "opacity-0" : "opacity-100",
              )}
            >
              {autocompleteEnabled && searchValue && (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-sm text-purple-800"
                    onClick={() => setSearchValue("")}
                  >
                    {searchValue}
                    <Icon icon="CLOSE" size={0.55} />
                  </button>
                  <button
                    type="button"
                    className="text-xs text-zinc-500"
                    onClick={reset}
                  >
                    Clear all
                  </button>
                </div>
              )}
              {showSortComparison ? (
                <DiscoverySortComparison
                  onExit={() => setIsComparingSort(false)}
                />
              ) : (
                <>
                  <DiscoverySortComparisonEntry
                    onStartComparison={() => setIsComparingSort(true)}
                  />
                  {discoveryListQuery.isLoading ||
                  discoverySupplierListQuery.isLoading ? (
                    <SpinnerLegacy />
                  ) : (
                    <>
                      <DiscoveryV2SupplierSearchResults
                        suppliers={discoverySupplierListQuery.data}
                      />
                      <DiscoveryV2ProductSearchResults
                        discoveryListQuery={discoveryListQuery}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <BestPricePromiseDialog
            productName={bestPricePromiseProduct?.name}
            supplierName={bestPricePromiseProduct?.Supplier.Business.name}
            buyerName={business?.name}
            active={bestPricePromiseDialogActive}
            onDismiss={() => setBestPricePromiseDialogActive(false)}
          />
        </RouteScrollArea>
      </div>
      <DiscoveryV2ProductDialog
        source="market"
        setBestPricePromiseDialogActive={(product) => {
          setBestPricePromiseDialogActive(true);
          setBestPricePromiseProduct(product);
        }}
        active={isDialogActive}
        ParentId={selectedProductParentId ?? 0}
        ProductItemId={selectedProductId ?? 0}
        onDismiss={() => setSelectedProductIds(undefined)}
      />
      {business?.type === "retailer" && (
        <DraftBasketDrawer
          active={draftBasketDrawerActive}
          setActive={setDraftBasketDrawerActive}
          showDeliveredBySellarFeatures={
            sdConsolidatedCheckoutEnabled && canShowDeliveredBySellar
          }
        />
      )}
    </MarketBasketFeedbackProvider>
  );
};

const DiscoveryV2ProductSearchResults = ({
  discoveryListQuery,
}: {
  discoveryListQuery: UseInfiniteQueryResult<
    InfiniteData<DiscoveryV2ListResponseType>
  >;
}) => {
  const canShowDeliveredBySellarOptionV2 =
    useCanShowDeliveredBySellarOptionV2();
  const {
    searchValue,
    orderBy,
    isDeliveredBySellar,
    setSelectedProductIds,
    setSupplierId,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      searchValue: state.searchValue,
      orderBy: state.orderBy,
      isDeliveredBySellar: state.isDeliveredBySellar,
      setSelectedProductIds: state.setSelectedProductIds,
      setSupplierId: state.setSupplierId,
    })),
  );

  const { track } = useEvents();
  const recommendedSortEnabled = useRemoteFeatureFlags().isActive(
    "recommendedDiscoverySearch",
  );
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { sessionId } = useMarketStorefrontSessionId();
  return (
    <>
      {isDesktop &&
        !(isDeliveredBySellar && canShowDeliveredBySellarOptionV2) && (
          <Card
            className={twMerge(
              "bg-white",
              "sticky top-2 z-10 flex flex-col justify-between gap-2 border p-4 sm:flex-row",
            )}
          >
            <div className={"hidden sm:block"}>
              <H4>
                {[
                  "Products matching your search",
                  !!searchValue && `for "${searchValue}":`,
                ]
                  .filter(Boolean)
                  .join(" ")}
              </H4>
              <H6>Use the filters provided to narrow down your results</H6>
            </div>
            <OrderBySelector />
          </Card>
        )}
      <div className={"flex flex-col gap-2"}>
        {!discoveryListQuery.data?.pages[0]?.length ? (
          <NoResults />
        ) : (
          discoveryListQuery.data.pages.map((page) =>
            page.map((product) => (
              <DiscoveryV2SearchCard
                product={product}
                onClick={() => {
                  setSupplierId(product.Supplier.id);
                  setSelectedProductIds({
                    productItemId: product.id,
                    parentProductId: product.Parent?.id,
                  });
                  const productIsPreOrder =
                    !!findLatestPreOrderAvailableFromDate([
                      { availableFrom: product.availableFrom },
                    ]);

                  track("Discover product clicked", {
                    supplier: product.Supplier.urlId,
                    product: product.name,
                    source: "Search result",
                    sortingOrder:
                      recommendedSortEnabled && orderBy === "popular"
                        ? "Recommended"
                        : orderByTypes[orderBy],
                    isPreOrder: productIsPreOrder,
                    sessionId,
                    ...getSellarWebsiteUtms(),
                  });
                  if (product.Parent)
                    useDiscoveryV2RecentlyViewedStore.getState().add({
                      id: product.Parent.id,
                      supplierId: product.Supplier.id,
                      name: product.name?.split(" • ")[0] ?? "",
                      imageUrl: product.Parent.imageUrl,
                      logoUrl: product.Supplier.Business.logoUrl,
                      urlId: product.Supplier.urlId,
                      details: `${product.Parent.kind} // ${product.Parent.abv ?? ""}%`,
                      expiry: setRecentlyViewedExpiry(),
                      containsPreOrderProductItem:
                        product.Parent.containsPreOrderProductItem,
                    });
                }}
                key={product.id}
              />
            )),
          )
        )}
      </div>
      <IntersectingInfiniteLoadingButton query={discoveryListQuery} />
      {!!discoveryListQuery.data?.pages[0]?.length && (
        <div className="mt-4">
          <IncompleteListUi />
        </div>
      )}
    </>
  );
};

const DiscoveryV2SupplierSearchResults = ({
  suppliers,
}: {
  suppliers: InfiniteData<DiscoverySupplierListResponseType> | undefined;
}) => {
  const { sessionId } = useMarketStorefrontSessionId();
  const { searchValue, isDeliveredBySellar, setSelectedSuppliers } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        searchValue: state.searchValue,
        isDeliveredBySellar: state.isDeliveredBySellar,
        setSelectedSuppliers: state.setSelectedSuppliers,
      })),
    );
  const sdConsolidatedCheckoutEnabled = useRemoteFeatureFlags().isActive(
    "sdConsolidatedCheckout",
  );
  const navigateToChild = useNavigateToChild();
  const { track } = useEvents();

  const hasSupplierResults = !!suppliers?.pages[0]?.length;
  const hasSearchValue = searchValue.trim().length > 0;

  if (!hasSupplierResults || !hasSearchValue) return null;

  return (
    <>
      <Card className={twMerge("bg-white", "border p-4")}>
        <H4>Suppliers matching your search for "{searchValue}":</H4>
      </Card>
      <div>
        <div
          className={
            "grid snap-x auto-cols-[calc(100%-2rem)] grid-flow-col grid-rows-1 gap-4 overflow-auto overflow-x-auto @2xl:auto-cols-[calc(50%-2rem)]"
          }
        >
          {suppliers.pages.map((page) =>
            page.map((supplier) => (
              <DiscoveryV2SupplierSearchCard
                key={supplier.id}
                supplier={supplier}
                onClick={() => {
                  if (isDeliveredBySellar && sdConsolidatedCheckoutEnabled) {
                    const name = supplier.Business.name;
                    setSelectedSuppliers([{ id: supplier.id, name }]);
                    return;
                  }

                  navigateToChild(
                    ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId }),
                  );
                  track("Discover supplier clicked", {
                    supplier: supplier.urlId,
                    hasSearchTerm: !!searchValue,
                    source: "Search result",
                    sessionId,
                    ...getSellarWebsiteUtms(),
                  });
                }}
              />
            )),
          )}
        </div>
      </div>
    </>
  );
};

const DiscoveryV2SupplierSearchCard = ({
  supplier,
  onClick,
}: {
  onClick?: () => void;
  supplier: DiscoverySupplierListResponseType[number];
}) => {
  return (
    <Card
      className={twMerge("bg-white", "cursor-pointer snap-center")}
      onClick={onClick}
    >
      <CardImage
        className={"h-[160px]"}
        imageUrl={supplier.imageUrl ?? ""}
        fallback="product-hero"
      />
      <CardContent className={"flex flex-col gap-2 p-4"}>
        <div className={"flex gap-2"}>
          <ProfilePicture
            size={"large"}
            logoUrl={supplier.Business.logoUrl}
            fallback="supplier-badge"
          />
          <div>
            <H5>{supplier.Business.name}</H5>
            <H6>
              {[
                supplier.Business.DeliveryAddress.city,
                supplier.Business.DeliveryAddress.region,
              ]
                .filter(Boolean)
                .join(", ")}
            </H6>
          </div>
        </div>
        <div className={"flex gap-1"}>
          {supplier.containerTypes.map((containerType) => (
            <Badge key={containerType}>{containerType}</Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const DiscoveryV2SearchCard = ({
  product,
  onClick,
}: {
  product: DiscoveryV2ListResponseType[number];
  onClick?: () => void;
}) => {
  const { sessionId } = useMarketStorefrontSessionId();
  const { isDisplaySizeSatisfied, target } = useDisplaySizeVariant();
  const { searchValue, isDeliveredBySellar, setSelectedSuppliers } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        searchValue: state.searchValue,
        isDeliveredBySellar: state.isDeliveredBySellar,
        setSelectedSuppliers: state.setSelectedSuppliers,
      })),
    );

  const [selectedSingleSupplierId, setSelectedSingleSupplierId] = useState<
    number | null
  >(null);

  const navigateToChild = useNavigateToChild();
  const { track } = useEvents();
  const isLg = isDisplaySizeSatisfied("lg");

  const [productName, productContainer] = product.name?.split(" • ") ?? [];

  const sellarCreditEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );

  const sdConsolidatedCheckoutEnabled = useRemoteFeatureFlags().isActive(
    "sdConsolidatedCheckout",
  );

  const canShowSellarCredit =
    sellarCreditEnabled && !product.Supplier.hasOptedOutSellarCredit;

  const canShowAddToBasket =
    sdConsolidatedCheckoutEnabled && isDeliveredBySellar;

  return (
    <>
      <Card
        ref={target}
        className={twMerge("bg-white", "@container")}
        onClick={onClick}
      >
        <CardHeader
          className={twMerge(
            "bg-white",
            "grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 p-3 @lg:gap-x-4",
          )}
        >
          <div
            className={twMerge(
              "col-span-2 flex justify-between gap-4 @lg:col-span-1",
            )}
          >
            <div className="flex flex-none items-center gap-2">
              <Badge
                className={"gap-1 uppercase"}
                onClick={(event) => {
                  if (isDeliveredBySellar && sdConsolidatedCheckoutEnabled) {
                    event.stopPropagation();
                    const name = product.Supplier.Business.name;
                    setSelectedSuppliers([{ id: product.Supplier.id, name }]);
                    setSelectedSingleSupplierId(product.Supplier.id);
                    return;
                  }
                  navigateToChild(
                    ORDER_BUYER.href({
                      supplierIdOrUrlId: product.Supplier.urlId,
                    }),
                  );
                  track("Discover supplier clicked", {
                    supplier: product.Supplier.urlId,
                    hasSearchTerm: !!searchValue,
                    source: "Search result - supplier badge",
                    sessionId,
                    ...getSellarWebsiteUtms(),
                  });
                }}
              >
                <span className="flex flex-none items-center gap-2">
                  <ProfilePicture
                    size={"small"}
                    logoUrl={product.Supplier.Business.logoUrl}
                    fallback="supplier-badge"
                  />
                  {product.Supplier.Business.name}
                </span>
              </Badge>
            </div>
            <div className={"flex min-w-1/2 justify-end gap-1"}>
              {canShowSellarCredit && <SellarCreditBadgeWithTooltip />}
              <DietaryDisplayList
                dietaryRequirements={{
                  VG: Boolean(product.Parent?.vegan),
                  LF: Boolean(product.Parent?.lactoseFree),
                  GF: Boolean(product.Parent?.glutenFree),
                }}
                mode="badge"
              />
            </div>
          </div>
          <ProfilePicture
            className={twMerge("col-start-1 @lg:row-span-2 @lg:row-start-1")}
            size={isLg ? "xl" : "large"}
            logoUrl={product.Parent?.imageUrl}
            fallback="supplier-badge"
          />
          <div className={"flex gap-2"}>
            <div className="w-full">
              <H5>{productName}</H5>
              {!canShowAddToBasket || isLg ? (
                <H6>
                  {product.Parent?.kind}
                  {" // "}
                  {product.Parent?.abv}%
                </H6>
              ) : (
                <H6 className="flex w-full items-center justify-between gap-2">
                  <span>
                    {product.Parent?.kind} / {product.Parent?.abv}% /{" "}
                    {productContainer}
                  </span>
                  <StockIndicator
                    canBackorder={product.canBackorder}
                    type="minimal"
                    stock={product.availableStock}
                  />
                </H6>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className={"flex flex-col gap-2 p-3"}>
          <div className="flex items-center justify-between">
            {(!canShowAddToBasket || isLg) && (
              <div className={"flex w-full items-center gap-2"}>
                <span>{productContainer}</span>
                <StockIndicator
                  canBackorder={product.canBackorder}
                  type={isLg ? "full" : "minimal"}
                  stock={product.availableStock}
                />
              </div>
            )}

            <div
              className={twMerge(
                "flex items-center justify-between gap-2",
                canShowAddToBasket && "rounded-lg bg-gray-100 px-3 py-2",
                canShowAddToBasket && !isLg && "w-full",
              )}
            >
              <Tooltip>
                <TooltipTrigger>
                  <div
                    className={
                      "tx-gray-700 rounded-sm bg-gray-100 px-2 py-1 text-sm font-bold"
                    }
                  >
                    {product.price != null
                      ? product.priceIsFromConnection
                        ? formatCurrency(product.price)
                        : `~${formatCurrency(product.price)}`
                      : "£ Pricing on request"}
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  className={
                    "Tooltip rounded-lg bg-black p-2 text-sm text-white"
                  }
                >
                  {product.price != null ? (
                    product.priceIsFromConnection ? (
                      <b>Your price</b>
                    ) : (
                      <>
                        <b>List price:</b> Your pricing maybe different
                      </>
                    )
                  ) : (
                    "Price has not been set. Contact the supplier for the correct price."
                  )}
                </TooltipContent>
              </Tooltip>
              {canShowAddToBasket && (
                <DiscoveryV2AddToBasketCta
                  product={product}
                  className={twMerge(!isLg && "max-w-33")}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <SingleSupplierSearchDialogProvider
        open={!!selectedSingleSupplierId}
        onDismiss={() => setSelectedSingleSupplierId(null)}
        supplierId={selectedSingleSupplierId}
      >
        {({ supplier, onDismiss }) => (
          <SingleSupplierSearchDialogContent
            supplier={supplier}
            onDismiss={onDismiss}
          />
        )}
      </SingleSupplierSearchDialogProvider>
    </>
  );
};

const FilterMenu = ({
  searchValue,
  setSearchValue,
  onSearchSubmitted,
  onFilterChanged,
}: {
  searchValue: string;
  setSearchValue: (searchValue: string) => void;
  onSearchSubmitted: (searchValue: string) => void;
  onFilterChanged: () => void;
}) => {
  const canShowDeliveredBySellarOptionV2 =
    useCanShowDeliveredBySellarOptionV2();

  const [priceTypeCategory, setPriceTypeCategory] = useState<
    PriceTypeCategory | undefined
  >();
  const {
    setMenuActive,
    isInStockItemsOnly,
    setIsInStockItemsOnly,
    isMySuppliersOnly,
    setIsMySuppliersOnly,
    isDirectDelivery,
    setIsDirectDelivery,
    isSellarCreditEligibleOnly,
    setIsSellarCreditEligibleOnly,
    isDeliveredBySellar,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setMenuActive: state.setMenuActive,
      isInStockItemsOnly: state.isInStockItemsOnly,
      setIsInStockItemsOnly: state.setIsInStockItemsOnly,
      isMySuppliersOnly: state.isMySuppliersOnly,
      setIsMySuppliersOnly: state.setIsMySuppliersOnly,
      isDirectDelivery: state.isDirectDelivery,
      setIsDirectDelivery: state.setIsDirectDelivery,
      isSellarCreditEligibleOnly: state.isSellarCreditEligibleOnly,
      setIsSellarCreditEligibleOnly: state.setIsSellarCreditEligibleOnly,
      isDeliveredBySellar: state.isDeliveredBySellar,
    })),
  );

  const sellarCreditEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );

  const canShowSellarCreditOption = sellarCreditEnabled;

  return (
    <div
      onChangeCapture={onFilterChanged}
      className={
        "flex h-full min-h-0 flex-col gap-2 overflow-auto overscroll-contain pb-4"
      }
    >
      <FilterCard className={"flex-col gap-2"}>
        <H6>Keyword Search</H6>
        <DiscoveryMarketSearch
          fullWidth
          onCommit={(selection) => onSearchSubmitted(selection.label)}
          initialValue={searchValue}
          onSubmit={(searchValue) => {
            setSearchValue(searchValue);
            onSearchSubmitted(searchValue);
            setMenuActive(false);
          }}
        />
      </FilterCard>
      {canShowDeliveredBySellarOptionV2 && <DeliveredBySellarOptionV2 />}
      <FilterCard className={"flex flex-col gap-6"}>
        {!isDeliveredBySellar && !canShowDeliveredBySellarOptionV2 && (
          <FilterSwitch
            title={"Suppliers delivering direct"}
            checked={isDirectDelivery}
            onChange={(checked) => setIsDirectDelivery(checked)}
          />
        )}
        <FilterSwitch
          title={"My Suppliers only"}
          checked={isMySuppliersOnly}
          onChange={(checked) => setIsMySuppliersOnly(checked)}
        />
      </FilterCard>
      <FilterCard>
        <FilterSwitch
          title={"In-stock items only"}
          checked={isInStockItemsOnly}
          onChange={(checked) => setIsInStockItemsOnly(checked)}
        />
      </FilterCard>
      {canShowSellarCreditOption && (
        <FilterCard>
          <FilterSwitch
            title={
              <div className="flex gap-2">
                Buy with
                <SellarIconGradient />
                <span className="bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text font-semibold text-transparent">
                  Sellar Credit
                </span>
              </div>
            }
            checked={isSellarCreditEligibleOnly}
            onChange={(checked) => setIsSellarCreditEligibleOnly(checked)}
          />
        </FilterCard>
      )}
      <FilterContainerCard />
      <FilterSupplierCard />
      <FilterPriceCard
        setPriceTypeCategory={setPriceTypeCategory}
        priceTypeCategory={priceTypeCategory}
      />
      <FilterStyleCard />
      <FilterAbvCard />
      <FilterDietaryCard />
    </div>
  );
};

const NoResults = () => {
  const { setSearchValue } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setSearchValue: state.setSearchValue,
    })),
  );

  return (
    <div className={"flex flex-col gap-6"}>
      <H4>No results</H4>
      <p>Start a new search?</p>
      <DiscoverySuggestionTagContainer>
        {randomizeDiscoverySuggestionTags().map((tag) => (
          <DiscoverySuggestionTag
            key={tag}
            tag={tag}
            onClick={() => setSearchValue(tag)}
          />
        ))}
      </DiscoverySuggestionTagContainer>
      <IncompleteListUi />
    </div>
  );
};

const SellarCreditBadgeWithTooltip = () => {
  return (
    <Tooltip placement="top">
      <TooltipTrigger>
        <BadgePurple>
          <SellarIconGradient />
        </BadgePurple>
      </TooltipTrigger>

      <TooltipContent
        className={
          "Tooltip flex flex-col gap-2 rounded-xl bg-white px-4 py-3 text-sm shadow-sm"
        }
      >
        <div className="flex flex-col">
          <span className="font-semibold text-zinc-800">
            Buy with Sellar Credit
          </span>
          <p className="text-zinc-700">
            Order now and pay nothing until{" "}
            {dayjs().endOf("month").add(30, "days").format("Do MMMM")}
          </p>
        </div>
        <a
          href="https://sellar.io/sellar-credit-for-buyers"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 px-0 py-0.5 text-xs font-semibold uppercase hover:bg-transparent"
        >
          <span>Learn more</span>
          <Icon
            icon="CHEVRON_RIGHT"
            className="relative bottom-[1px]"
            size={0.7}
          />
        </a>
      </TooltipContent>
    </Tooltip>
  );
};
